import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { rpcClient, orpc } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

interface BulkRerunResult {
  invoiceId: string;
  success: boolean;
  error?: string;
}

interface BulkRerunState {
  isLoading: boolean;
  results: BulkRerunResult[];
}

export function useBulkRerunAPInvoices() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [state, setState] = useState<BulkRerunState>({
    isLoading: false,
    results: [],
  });

  const bulkRerun = useCallback(
    async (invoiceIds: string[]) => {
      if (invoiceIds.length === 0) return [];

      setState({ isLoading: true, results: [] });

      try {
        const response = await rpcClient.workflows.rerunInvoices({
          invoiceIds,
        });

        const results = response.results;
        const successCount = results.filter((r) => r.success).length;
        const failureCount = results.filter((r) => !r.success).length;

        setState({ isLoading: false, results });

        if (failureCount === 0) {
          showToast({
            title: 'Rerun Started',
            message: `Successfully queued ${successCount} invoice${successCount !== 1 ? 's' : ''} for rerun`,
            color: 'success',
            icon: 'check',
          });
        } else if (successCount === 0) {
          showToast({
            title: 'Rerun Failed',
            message: `Failed to rerun all ${failureCount} invoice${failureCount !== 1 ? 's' : ''}`,
            color: 'error',
            icon: 'error',
          });
        } else {
          showToast({
            title: 'Rerun Partially Complete',
            message: `Queued ${successCount} of ${results.length} invoices for rerun. ${failureCount} failed.`,
            color: 'warning',
            icon: 'warning',
          });
        }

        queryClient.invalidateQueries({
          queryKey: orpc.accountsPayable.invoices.list.queryKey({ input: {} }),
        });

        return results;
      } catch (error) {
        setState({ isLoading: false, results: [] });
        showToast({
          title: 'Rerun Failed',
          message:
            error instanceof Error ? error.message : 'Unknown error occurred',
          color: 'error',
          icon: 'error',
        });
        return [];
      }
    },
    [queryClient, showToast],
  );

  const reset = useCallback(() => {
    setState({ isLoading: false, results: [] });
  }, []);

  const successCount = state.results.filter((r) => r.success).length;
  const failureCount = state.results.filter((r) => !r.success).length;

  return {
    bulkRerun,
    reset,
    isLoading: state.isLoading,
    results: state.results,
    successCount,
    failureCount,
  };
}
